import {getTestProps} from '@benchling/js-test-utils';
import {Button, Stack, Text, createStyles} from '@mantine/core';
import React from 'react';

import staticURL from 'lib/staticURL';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import type {ModelHubIllustration} from './modelHubIllustrations';

interface ModelHubEmptyStateProps {
  illustration: ModelHubIllustration;
  title: string;
  description: string;
  // When provided, renders a primary "New prediction" call to action.
  onNewPrediction?: () => void;
}

/**
 * Centered empty state for the standalone Model Hub list pages: an illustration above a heading,
 * one-line description, and an optional "New prediction" CTA.
 */
export default function ModelHubEmptyState({
  illustration,
  title,
  description,
  onNewPrediction,
}: ModelHubEmptyStateProps): JSX.Element {
  const {classes} = useStyles({illustrationHeightPx: illustration.displayHeightPx});

  return (
    <Stack align="center" className={classes.root} spacing="xl">
      {/* Decorative: the heading below carries the meaning. */}
      <img
        alt=""
        aria-hidden={true}
        className={classes.illustration}
        height={illustration.intrinsicHeight}
        src={staticURL(illustration.path)}
        width={illustration.intrinsicWidth}
        {...getTestProps({component: 'ModelHubEmptyState', element: 'illustration'})}
      />
      <Text className={classes.title} component="h2">
        {title}
      </Text>
      <Text className={classes.description}>{description}</Text>
      {onNewPrediction != null && (
        <Button
          leftIcon={<BenchlingIcon icon="plus" />}
          onClick={onNewPrediction}
          radius="sm"
          variant="primary"
        >
          New prediction
        </Button>
      )}
    </Stack>
  );
}

const useStyles = createStyles((theme, {illustrationHeightPx}: {illustrationHeightPx: number}) => ({
  root: {
    flex: 1,
    justifyContent: 'center',
    padding: '40px 24px',
  },
  illustration: {
    height: illustrationHeightPx,
    userSelect: 'none',
    width: 'auto',
  },
  title: {
    color: theme.black,
    fontSize: 19,
    fontWeight: 600,
  },
  description: {
    color: theme.colors.gray[7],
    fontSize: 14,
    maxWidth: 400,
    textAlign: 'center' as const,
  },
}));
